import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

const TESTIMONIALS = [
  {
    quote: "Krijo transformed our hiring pipeline. Their RPO team filled 40+ niche roles in a single quarter — with quality we hadn't seen from any previous partner.",
    name: "Priya Sharma",
    role: "HR Director, FinEdge Technologies",
  },
  {
    quote: "Their ERPNext implementation cut our manual processing time by 60%. The team understood our business before writing a single line of configuration.",
    name: "Rahul Mehta",
    role: "COO, Arvind Manufacturing Co.",
  },
  {
    quote: "From contractual staffing to a custom mobile app, Krijo has been our single partner for both people and technology. Rare combination, executed brilliantly.",
    name: "Ananya Iyer",
    role: "Founder & CEO, LogiStack Solutions",
  },
  {
    quote: "Responsive, transparent, and genuinely invested in our success. The IT support team feels like an extension of our own company.",
    name: "Vikram Desai",
    role: "CTO, NovaRetail Group",
  },
];

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute bottom-0 -right-32 w-[420px] h-[420px] rounded-full bg-[#F89335]/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F89335]">Testimonials</p>
          <h2 data-testid="testimonials-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            What Our <span className="text-gradient-gold">Clients Say</span>
          </h2>
        </motion.div>

        <div className="mt-12 relative">
          <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
            <div className="flex -ml-6">
              {TESTIMONIALS.map((t, i) => (
                <div key={t.name} className="flex-[0_0_100%] md:flex-[0_0_50%] pl-6 min-w-0">
                  <div data-testid={`testimonial-card-${i + 1}`} className="glass-card rounded-2xl p-8 h-full flex flex-col">
                    <Quote size={28} className="text-[#F89335]/60" />
                    <div className="flex gap-1 mt-4">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={15} className="fill-[#F9AE46] text-[#F9AE46]" />
                      ))}
                    </div>
                    <p className="mt-4 text-slate-300 text-sm sm:text-base leading-relaxed flex-1">"{t.quote}"</p>
                    <div className="mt-6 pt-5 border-t border-white/10">
                      <p className="font-['Poppins'] font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i + 1}`}
                  onClick={() => emblaApi?.scrollTo(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${selected === i ? "w-8 bg-[#F89335]" : "w-2 bg-white/20 hover:bg-white/40"}`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                data-testid="testimonial-prev-button"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label="Previous testimonial"
                className="btn-ghost-glass w-11 h-11 rounded-full flex items-center justify-center text-white"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="testimonial-next-button"
                onClick={() => emblaApi?.scrollNext()}
                aria-label="Next testimonial"
                className="btn-ghost-glass w-11 h-11 rounded-full flex items-center justify-center text-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
